import {
    mdiWebhook,
    mdiRobotOutline,
    mdiServerNetwork,
    mdiTicketOutline,
    mdiAccount,
    mdiAccountDetails,
    mdiAccountGroup,
    mdiApps,
    mdiAutoFix,
    mdiCardAccountDetailsOutline,
    mdiClipboardTextOutline,
    mdiClipboardCheckMultipleOutline,
    mdiDomain,
    mdiFileCogOutline,
    mdiBriefcase,
    mdiBullseyeArrow,
    mdiChartBoxOutline,
    mdiChartScatterPlot,
    mdiChartTimelineVariant,
    mdiReceiptTextOutline,
    mdiBug,
    mdiBookOpenPageVariantOutline,
    mdiCalendarClock,
    mdiCashMultiple,
    mdiClipboardCheckOutline,
    mdiCog,
    mdiDatabase,
    mdiEmailFastOutline,
    mdiEmailOutline,
    mdiFileDocument,
    mdiFileExport,
    mdiFileImport,
    mdiFileSign,
    mdiFileTree,
    mdiFingerprint,
    mdiFlagVariant,
    mdiFloorPlan,
    mdiFolder,
    mdiForumOutline,
    mdiFunction,
    mdiGauge,
    mdiGavel,
    mdiGraphOutline,
    mdiHistory,
    mdiHome,
    mdiKey,
    mdiKeyChain,
    mdiSignRealEstate,
    mdiAccountFileTextOutline,
    mdiLogout,
    mdiMagnify,
    mdiOfficeBuilding,
    mdiPalette,
    mdiPencilRuler,
    mdiPinwheel,
    mdiQrcode,
    mdiRefresh,
    mdiShape,
    mdiShieldAccount,
    mdiShieldAlert,
    mdiShieldLockOutline,
    mdiSigma,
    mdiSwapHorizontal,
    mdiTagMultiple,
    mdiTrayArrowDown,
    mdiVoteOutline,
    mdiSignatureFreehand,
    mdiTuneVariant,
    mdiVectorLink,
    mdiWrenchClock, mdiTrendingUp } from '@mdi/js'

/**
 * One MDI path per section of the app — the single source of truth for
 * "which icon means this part of the product".
 *
 * Two consumers read this table: `AppSidebar` (the nav entries) and
 * `PageHeader` (the square to the left of every page title). Because both
 * read the same key, a page's header icon IS the sidebar icon of its section
 * by construction — there is no second place to keep in sync, and no page can
 * quietly invent a different icon for a section that already has one.
 *
 * Sub-pages with no sidebar entry of their own (a create page, a settings
 * sub-route, the type registry) pick an entry from this same table rather
 * than importing a path from `@mdi/js` directly. When a genuinely new section
 * appears, add its key HERE first, and mirror it in `docs/frontend/icons.md`.
 *
 * Icons are MDI `@mdi/js` paths rendered through `AppIcon`, never lucide —
 * see
 * `docs/frontend/icons.md`.
 */
export const SECTION_ICONS = {
    // ---- Sidebar sections (group order mirrors AppSidebar) ----------------
    home: mdiHome,
    search: mdiMagnify,
    assets: mdiOfficeBuilding,
    contracts: mdiFileSign,
    // General assemblies: minutes and their resolutions (spec 2026-10-05-01).
    assemblies: mdiVoteOutline,
    // Leases and the rent roll (spec 2026-10-25-03).
    leases: mdiKeyChain,
    listings: mdiSignRealEstate,
    // Rental applications and screening (spec 2026-10-25-11).
    'rental-applications': mdiAccountFileTextOutline,
    // Signature requests on PDFs (spec 2026-10-05-05).
    signatures: mdiSignatureFreehand,
    // Organization and building rules, how-to-handle-X (spec 2026-10-10-06).
    knowledge: mdiBookOpenPageVariantOutline,
    // Data collection campaigns (spec 2026-10-20-12).
    campaigns: mdiClipboardCheckMultipleOutline,
    users: mdiAccountGroup,
    capex: mdiCalendarClock,
    conversations: mdiForumOutline,
    steering: mdiBullseyeArrow,
    // A resident's own requests (spec 2026-10-07-03).
    requests: mdiTicketOutline,
    group_steering: mdiChartBoxOutline,
    // The intra-portfolio cost benchmark (spec 2026-09-13-01).
    benchmark: mdiChartScatterPlot,
    documents: mdiFileDocument,
    expenses: mdiCashMultiple,
    interventions: mdiWrenchClock,
    // Written-up visits: the report attached to an intervention.
    intervention_reports: mdiClipboardTextOutline,
    // People outside the app: tenants, suppliers, syndics (not users, not stakeholder roles).
    contacts: mdiCardAccountDetailsOutline,
    data: mdiDatabase,
    imports: mdiTrayArrowDown,
    activity: mdiHistory,

    // ---- Chrome (sidebar footer / header affordances) ---------------------
    account: mdiAccount,
    settings: mdiCog,
    logout: mdiLogout,
    refresh: mdiRefresh,

    // ---- Organization & access -------------------------------------------
    organizations: mdiDomain,
    members: mdiAccountGroup,
    roles: mdiShieldAccount,
    accessGrants: mdiShieldAlert,
    security: mdiShieldLockOutline,
    webhooks: mdiWebhook,
    profile: mdiAccount,
    tokens: mdiKey,
    passkeys: mdiFingerprint,
    emailIngestion: mdiEmailFastOutline,
    intakeAddresses: mdiEmailOutline,

    // ---- The type registry / vocabulary ----------------------------------
    types: mdiShape,
    assetTypes: mdiShape,
    documentTypes: mdiBriefcase,
    contractTypes: mdiFileCogOutline,
    stakeholderTypes: mdiAccountDetails,
    relationTypes: mdiVectorLink,
    properties: mdiPencilRuler,
    folderLayouts: mdiFolder,
    tags: mdiTagMultiple,

    // ---- Data engineering -------------------------------------------------
    derivations: mdiFunction,
    normalizations: mdiAutoFix,
    aggregations: mdiSigma,
    mappingRules: mdiSwapHorizontal,
    businessRules: mdiGavel,
    dataQuality: mdiGauge,
    export: mdiFileExport,
    import: mdiFileImport,

    // ---- Compliance --------------------------------------------------------
    compliance: mdiClipboardCheckOutline,

    // ---- Operations --------------------------------------------------------
    jobs: mdiPinwheel,
    featureFlags: mdiFlagVariant,
    adminSettings: mdiTuneVariant,
    adminAgentPool: mdiServerNetwork,
    agentRun: mdiRobotOutline,
    adminUsage: mdiChartTimelineVariant,
    adminBilling: mdiReceiptTextOutline,
    adminRentIndices: mdiTrendingUp,
    debug: mdiBug,

    // ---- Visual / spatial --------------------------------------------------
    graph: mdiGraphOutline,
    hierarchy: mdiFileTree,
    planRendering: mdiFloorPlan,
    labels: mdiQrcode,

    // ---- Developer references ---------------------------------------------
    patterns: mdiApps,
    designReference: mdiPalette,
} as const

/**
 * The entities of the product. Each is drawn with ONE icon, in the sidebar,
 * a page header, a list row, a link, a badge or a card. A test asserts the
 * keys exist and that no two of them share a glyph. Rendered with the places
 * they appear at `/$org/design-reference#entity-icons`.
 */
export const ENTITY_ICON_KEYS = [
    'assets',
    'conversations',
    'contracts',
    'contacts',
    'signatures',
    'capex',
    'steering',
    'group_steering',
    'benchmark',
    'documents',
    'knowledge',
    'campaigns',
    'expenses',
    'interventions',
    'intervention_reports',
    'imports',
    'activity',
    'properties',
] as const satisfies readonly (keyof typeof SECTION_ICONS)[]

export type SectionIconKey = keyof typeof SECTION_ICONS
